import { authorizedFetch, API_ORIGIN } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

const BASE = `${API_ORIGIN}/api/talentco/platform`
const KEY = ['talentco', 'platform-area'] as const

export type PlatformActionStatus = 'running' | 'succeeded' | 'failed' | 'skipped' | 'interrupted'

export type PlatformActionRun = {
  id: string
  action: string
  action_label: string
  target_type: 'job' | 'profile'
  target_id: string
  target_label: string
  target_is_simulation: boolean
  status: PlatformActionStatus
  summary: string
  actor_email: string
  actor_name: string
  started_at: string
  finished_at: string | null
}

export type PlatformJob = {
  id: string
  title: string
  organisation_name: string
  publication_status: 'draft' | 'published' | 'closed' | 'archived'
  is_simulation: boolean
  public_path: string
  completeness_score: number | null
  completeness_missing: string[]
  low_confidence: boolean
  required_skills: number
  preferred_skills: number
  updated_at: string
  last_action: PlatformActionRun | null
}

export const JOB_STEPS = [
  'improve_job_description',
  'reparse_job_skills',
  'rescore_job_completeness',
  'rerun_job_analysis',
] as const
export type JobStep = (typeof JOB_STEPS)[number]

export type PlatformJobMatch = {
  profile_id: string
  name: string
  added_via: string
  match_percent: number
  was_llm_judged: boolean
  analysis_status: string
  evaluated_at: string | null
}

export type PlatformJobDetail = PlatformJob & {
  description: string
  skills: { required_skills: string[]; preferred_skills: string[] }
  steps: Record<JobStep, PlatformActionRun | null>
  matches: PlatformJobMatch[]
}

export type PlatformProfile = {
  id: string
  name: string
  email: string
  headline: string
  is_simulation: boolean
  skills: number
  evidenced_skills: number
  experiences: number
  cv: { filename: string; status: string; uploaded_at: string } | null
  completeness_score: number
  completeness_missing: string[]
  marked_for_improvement_at: string | null
  updated_at: string
  last_action: PlatformActionRun | null
}

export type Page<T> = { total: number; limit: number; offset: number; results: T[] }
export type PlatformActionLog = Page<PlatformActionRun> & {
  actions: { value: string; label: string }[]
}

export class PlatformAreaError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

async function readJson<T>(response: Response): Promise<T> {
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    const message = (body as { detail?: string }).detail
    throw new PlatformAreaError(message || 'Something went wrong. Please try again.', response.status)
  }
  return body as T
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '' || value === false) continue
    search.set(key, String(value))
  }
  return search.toString()
}

const hasRunning = (runs: (PlatformActionRun | null)[]) => runs.some((run) => run?.status === 'running')
const POLL_MS = 3000

export type PlatformListFilters = {
  q: string
  includeSimulated: boolean
  offset: number
  limit: number
  status?: string
}

export function usePlatformJobs(filters: PlatformListFilters) {
  const qs = query({
    q: filters.q.trim(),
    status: filters.status,
    include_simulated: filters.includeSimulated,
    limit: filters.limit,
    offset: filters.offset,
  })
  return useQuery({
    queryKey: [...KEY, 'jobs', qs],
    queryFn: async () => readJson<Page<PlatformJob>>(await authorizedFetch(`${BASE}/jobs?${qs}`)),
    refetchInterval: (q) =>
      hasRunning((q.state.data?.results ?? []).map((row) => row.last_action)) ? POLL_MS : false,
  })
}

export function usePlatformProfiles(filters: PlatformListFilters) {
  const qs = query({
    q: filters.q.trim(),
    include_simulated: filters.includeSimulated,
    limit: filters.limit,
    offset: filters.offset,
  })
  return useQuery({
    queryKey: [...KEY, 'profiles', qs],
    queryFn: async () => readJson<Page<PlatformProfile>>(await authorizedFetch(`${BASE}/profiles?${qs}`)),
    refetchInterval: (q) =>
      hasRunning((q.state.data?.results ?? []).map((row) => row.last_action)) ? POLL_MS : false,
  })
}

const ANALYSIS_IN_FLIGHT = new Set(['pending', 'running'])

export function usePlatformJob(jobId: string) {
  return useQuery({
    queryKey: [...KEY, 'job', jobId],
    queryFn: async () => readJson<PlatformJobDetail>(await authorizedFetch(`${BASE}/jobs/${jobId}`)),
    enabled: Boolean(jobId),
    refetchInterval: (q) => {
      const data = q.state.data
      if (!data) return false
      const busy =
        hasRunning(Object.values(data.steps)) ||
        data.matches.some((match) => ANALYSIS_IN_FLIGHT.has(match.analysis_status))
      return busy ? POLL_MS : false
    },
  })
}

export type PlatformActionLogFilters = {
  targetType: string
  targetId: string
  action: string
  includeSimulated: boolean
  offset: number
  limit: number
}

export function usePlatformActionLog(filters: PlatformActionLogFilters) {
  const qs = query({
    target_type: filters.targetType,
    target_id: filters.targetId,
    action: filters.action,
    include_simulated: filters.includeSimulated,
    limit: filters.limit,
    offset: filters.offset,
  })
  return useQuery({
    queryKey: [...KEY, 'actions', qs],
    queryFn: async () => readJson<PlatformActionLog>(await authorizedFetch(`${BASE}/actions?${qs}`)),
    refetchInterval: (q) => (hasRunning(q.state.data?.results ?? []) ? POLL_MS : false),
  })
}

export type PlatformAction =
  | { kind: 'rescore'; jobId: string }
  | { kind: 'reparse-job'; jobId: string }
  | { kind: 'rerun-analysis'; jobId: string }
  | { kind: 'mark-profile'; profileId: string; note: string }
  | { kind: 'reparse-profile'; profileId: string }

function actionRequest(action: PlatformAction): [string, RequestInit] {
  const post: RequestInit = { method: 'POST', headers: { 'Content-Type': 'application/json' } }
  switch (action.kind) {
    case 'rescore':
      return [`${BASE}/jobs/${action.jobId}/rescore`, post]
    case 'reparse-job':
      return [`${BASE}/jobs/${action.jobId}/reparse-skills`, post]
    case 'rerun-analysis':
      return [`${BASE}/jobs/${action.jobId}/rerun-analysis`, post]
    case 'mark-profile':
      return [
        `${BASE}/profiles/${action.profileId}/mark-for-improvement`,
        { ...post, body: JSON.stringify({ note: action.note }) },
      ]
    case 'reparse-profile':
      return [`${BASE}/profiles/${action.profileId}/reparse`, post]
  }
}

export function usePlatformAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (action: PlatformAction) => {
      const [url, init] = actionRequest(action)
      return readJson<PlatformActionRun>(await authorizedFetch(url, init))
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: KEY })
    },
  })
}

export function invalidatePlatformArea(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: KEY })
}

export type RubricCriterion = {
  key: string
  kind: string
  label: string
  definition: string
  weight: number | null
  component_skills: string[]
  anchors: string[]
}

export type GateSummary = {
  labelled?: number | null
  agreed?: number | null
  agreement_ratio?: number | null
  exact_ratio?: number | null
  pairwise_order_ratio?: number | null
  mean_score?: number | null
  cache_misses?: number | null
}

export type GoldenGate = {
  mode: 'cached' | 'stub' | 'live'
  passed: boolean
  reason: string
  golden_set_version?: string | number | null
  baseline_source?: string
  baseline: GateSummary
  candidate: GateSummary
  band_changes?: unknown[]
  mean_abs_delta?: number | null
  checked_at?: string
}

export type RubricRevision = {
  id: string
  version: number
  status: 'proposed' | 'active' | 'retired'
  source_current: boolean
  criteria: RubricCriterion[]
  approved_by_email: string
  activated_at: string | null
  retired_at: string | null
  golden_gate: GoldenGate | Record<string, never>
  created_at: string
}

export type RubricDiffRow = {
  key: string
  label: string
  kind: string
  change: 'added' | 'removed' | 'changed' | 'unchanged'
  weight_before: number | null
  weight_after: number | null
  skills_added: string[]
  skills_removed: string[]
  skills: string[]
  definition_changed: boolean
}

export const RUBRIC_STEPS = [
  'draft_job_rubric',
  'check_job_rubric',
  'activate_job_rubric',
  'retire_job_rubric',
] as const
export type RubricStep = (typeof RUBRIC_STEPS)[number]

export type JobRubricSection = {
  opportunity_id: string
  scoring_enabled: boolean
  gate_mode: string
  current: { source: 'job' | 'tenant'; label: string; criteria: RubricCriterion[] }
  draft: RubricRevision | null
  diff: RubricDiffRow[]
  revisions: RubricRevision[]
  steps: Record<RubricStep, PlatformActionRun | null>
}

export class GateBlockedError extends PlatformAreaError {
  gate: GoldenGate
  constructor(message: string, gate: GoldenGate) {
    super(message, 409)
    this.gate = gate
  }
}

export function usePlatformJobRubric(jobId: string) {
  return useQuery({
    queryKey: [...KEY, 'job', jobId, 'rubric'],
    queryFn: async () => readJson<JobRubricSection>(await authorizedFetch(`${BASE}/jobs/${jobId}/rubric`)),
    enabled: Boolean(jobId),
  })
}

export type RubricAction =
  | { kind: 'draft' }
  | { kind: 'check' | 'activate' | 'retire'; revisionId: string }

export function useRubricAction(jobId: string) {
  const queryClient = useQueryClient()
  const key = [...KEY, 'job', jobId, 'rubric']
  return useMutation({
    mutationFn: async (action: RubricAction) => {
      const path =
        action.kind === 'draft' ? 'draft' : `${action.revisionId}/${action.kind}`
      const response = await authorizedFetch(`${BASE}/jobs/${jobId}/rubric/${path}`, { method: 'POST' })
      if (response.status === 409) {
        const body = (await response.json().catch(() => ({}))) as { detail?: string; golden_gate?: GoldenGate }
        if (body.golden_gate) throw new GateBlockedError(body.detail || 'The golden set regressed.', body.golden_gate)
        throw new PlatformAreaError(body.detail || 'This rubric changed. Refresh and try again.', 409)
      }
      return readJson<JobRubricSection>(response)
    },
    onSuccess: (section) => queryClient.setQueryData(key, section),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: KEY }),
  })
}

export type BackfillKind = 'completeness' | 'marketplace-skills'

const BACKFILL_ENDPOINT: Record<BackfillKind, { path: string; redoParam: string }> = {
  completeness: { path: 'completeness-backfill', redoParam: 'rescore_all' },
  'marketplace-skills': { path: 'marketplace-skills-backfill', redoParam: 'reextract_all' },
}

export type BackfillRun = PlatformActionRun & {
  params: { rescore_all?: boolean; reextract_all?: boolean; cutoff?: string | null; chunk?: number }
  result: {
    total?: number
    processed?: number
    scored?: number
    low_confidence?: number
    extracted?: number
    trusted_links?: number
    proposals_created?: number
    failed?: number
  }
}

export function useBackfill(kind: BackfillKind) {
  const queryClient = useQueryClient()
  const { path, redoParam } = BACKFILL_ENDPOINT[kind]
  const key = [...KEY, 'backfill', kind]
  const status = useQuery({
    queryKey: key,
    queryFn: async () => readJson<BackfillRun | null>(await authorizedFetch(`${BASE}/jobs/${path}`)),
    refetchInterval: (q) => (q.state.data?.status === 'running' ? POLL_MS : false),
  })
  const start = useMutation({
    mutationFn: async (redoAll: boolean) =>
      readJson<BackfillRun>(
        await authorizedFetch(`${BASE}/jobs/${path}${redoAll ? `?${redoParam}=true` : ''}`, {
          method: 'POST',
        }),
      ),
    onSuccess: (run) => queryClient.setQueryData(key, run),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: key }),
  })
  return { status, start }
}
